<template lang="pug">
div
  template(v-if="readOnly")
    v-sheet.text-md.round-xl.border-menu(flat outlined min-width="150")
      v-row.py-1(no-gutters align="center" :style="`min-height: ${minHeight}px`")
        v-col.pl-3
          v-row(v-if="selectedItem.length > 0" no-gutters)
            template(v-for="val in selectedItem")
              v-col.shrink.pa-1
                v-chip.pl-2.pr-1(color="neutral300" label small)
                  span.pr-1.text-xs {{ val }}
  template(v-else)
    v-menu(v-model="show" rounded="lg" allow-overflow :close-on-content-click="false" :close-on-click="closeOnClick" offset-y :attach="attach" max-height="250" :z-index="zIndex")
      template(#activator="{ on, attrs }")
        v-sheet.text-md.round-xl(v-bind="attrs" v-on="on" :class="error ? 'error-border-menu' : 'border-menu'" flat outlined min-width="150")
          div.d-flex.align-center.py-1.px-2(:style="`min-height: ${minHeight}px`")
            div(v-if="selectedItem.length > 0")
              template(v-for="val in selectedItem")
                y-chip.ma-1(outlined color="neutral300" :label="val" size="xs")
                  template(#append)
                    v-btn.ml-1.mr-n1(icon x-small @click.stop="onDeselectItem(val)")
                      v-icon(size="14") mdi-close
            .text-sm.neutral500--text.ml-2(v-else) {{ $t('select_diagnosis') }}
            v-spacer
            v-icon(size="24") $yzi-chevron-down
        div.text-xs.danger500--text.ml-3.mt-1.mb-2(v-show="error") This field is required

      .text-xs.white
        .search-input
          v-text-field.mb-2.mx-4.text-sm.round-xl(v-model="itemName" outlined :placeholder="$t('search_or_add_new')" dense hide-details autofocus)
          v-divider
          template(v-if="showCreateButton")
            y-button(:label="`Create diagnosis '${itemName}'`" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="px-4" @click="onCreateNew")
            v-divider
        div.d-flex.justify-center(v-if="isFetching")
          v-progress-linear(:width="3" rounded color="primary" indeterminate)
        .d-flex.align-center.justify-space-between.px-4.py-3
          .font-semibold Existing diagnosis
          y-pagination(v-model="page" :pageSize="pageSize" :count="count" :loading="isFetching") 
        div(v-for="item in filteredItems")
          div.d-flex.align-center.px-4.py-3(v-if="isItemDisabled(item)" style="background-color: var(--v-neutral300)")
            div.text-sm.flex-grow-1.neutral700--text {{ item.value }}
          div.d-flex.align-center.px-4.py-3.clickable.hover-bg(v-else @click="onSelectItem(item)")
            div.text-sm.flex-grow-1.neutral700--text {{ item.value }}
            v-btn(x-small icon color="transparent" @click.stop="onDeleteItem(item)")
              v-icon(small color="danger500") $yzi-trash-03
          v-divider
        template(v-if="!filteredItems.length")
          .px-4.py-3 {{ $t('no_result_match') }}
  .dialogs
    delete-confirmation-dialog(v-if="openDeleteConfirmationDialog" :open.sync="openDeleteConfirmationDialog" :loading="isDeleting" @cancel="onCancelDeleteItem" @delete="onConfirmDeleteItem")
      delete-confirmation-details(:selectedDiagnose="toDeleteItem")
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import DeleteConfirmationDetails from '@/components/clinic/consultation/diagnosis/DeleteConfirmationDetails.vue'

export default {
  components: {
    DeleteConfirmationDetails, DeleteConfirmationDialog
  },
  props: {
    error: {
      type: Boolean
    },
    isIndependent: {
      type: Boolean
    },
    attach: {
      type: Boolean
    },
    parentDiagnosis: {
      type: Array,
      default: () => {[]}
    },
    leadPid: {
      type: String, 
      required: true
    },
    minHeight: {
      type: String,
      default: '40'
    },
    zIndex: {
      type: Number,
      default: null
    },
    readOnly: {
      type: Boolean
    }
  },
  data: () => ({
    isLoading: false,
    isFetching: false,
    show: false,
    itemName: null,
    diagnoses: [],
    toDeleteItem: null,
    isDeleting: false,
    openDeleteConfirmationDialog: false,
    selectedItem: [],
    closeOnClick: true,
    page: 1,
    pageSize: 20,
    count: 0,
  }),
  mounted() {
    this.selectedItem = [...this.parentDiagnosis]
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    showCreateButton() {
      if (this.itemName && (!this.filteredItems.length || !this.filteredItems.find(f => f.value.toLowerCase() === this.itemName.toLowerCase()))) {
        return true
      }
      return false
    },
    filteredItems() {
      if (this.itemName) {
        const keyword = this.itemName.toLowerCase()
        return this.diagnoses.filter(item => (item.value.toLowerCase().includes(keyword)))
      }
      return this.diagnoses
    },
  },
  watch: {
    show (val) {
      if (val) {
        this.itemName = ''
        this.fetchDiagnoses()
      }
    },
    selectedItem () {
      this.$emit('updated', this.selectedItem)
      if (this.isIndependent) {
        return
      }
      this.updateDiagnoses()
    },
    page () {
      this.fetchDiagnoses()
    },
    itemName () {
      this.searchStart()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchDiagnoses () {
      try {
        this.isFetching = true
        const params = {
          company: this.company.id,
          enabled: true,
          field: 'consultation.diagnosis',
          search: this.itemName || null,
          page: this.page,
          page_size: this.pageSize
        }
        const { data } = await this.$axios.get(`/options`, { params })
        this.diagnoses = data.results
        this.count = data.count
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    searchStart: debounce(function () {
      this.page = 1
      this.fetchDiagnoses()
    }, 750),
    onSelectItem (item) {
      this.selectedItem.push(item.value)
      this.itemName = ''
    },
    onDeselectItem (item) {
      this.closeOnClick = false
      this.selectedItem.splice(this.selectedItem.indexOf(item), 1)
      setTimeout(() => {
        this.closeOnClick = true
      }, 500);
    },
    isItemDisabled (item) {
      return this.selectedItem.find(selected => selected === item.value)
    },
    async onConfirmDeleteItem () {
      try {
        this.isDeleting = true
        await this.$axios.delete(`/options/${this.toDeleteItem.id}`)
        this.diagnoses = this.diagnoses.filter(diagnose => diagnose.id !== this.toDeleteItem.id)
        this.showMessage(['success', this.$t('success_message.successfully_updated')])
        this.openDeleteConfirmationDialog = false
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDeleting = false
        this.closeOnClick = true
      }
    },
    async onCreateNew () {
      try {
        this.isFetching = true
        let payload = {
          company: this.company.id,
          field: 'consultation.diagnosis',
          value: this.itemName,
          enabled: true
        }
        const { data } = await this.$axios.post(`/options`, payload)
        this.onSelectItem(data)
        this.diagnoses.push(data)
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    async updateDiagnoses () {
      try {
        this.isFetching = true
        const payload = {
          diagnoses: this.selectedItem
        }
        await this.$axios.patch(`/clinics/${this.company.slug}/consultations/${this.leadPid}`, payload)
        this.$emit('updated', this.selectedItem)
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    onDeleteItem (item) {
      this.toDeleteItem = item
      this.openDeleteConfirmationDialog = true
      this.closeOnClick = false
    },
    onCancelDeleteItem () {
      this.toDeleteItem = null
      this.openDeleteConfirmationDialog = false
      this.closeOnClick = true
    },
  }
}
</script>

<style scoped>
.border-menu {
  border: 1px solid var(--v-basic500);
  box-shadow: none;
}
.error-border-menu {
  border: 1px solid var(--v-danger500);
  box-shadow: none;
}
.search-input {
  position: sticky;
  top: 0px;
  background-color: #FFFFFF !important;
  border-color: #FFFFFF !important;
  z-index: 12;
  padding-top: 8px;
}
</style>